import React, { useState, useEffect, useRef } from 'react';
import {
  FiCamera,
  FiSave,
  FiCopy,
  FiCheck,
  FiExternalLink,
  FiShoppingBag,
  FiAlertCircle,
  FiImage,
  FiPlus,
  FiTrash2,
  FiTruck,
} from 'react-icons/fi';
import { IoStorefrontOutline } from 'react-icons/io5';
import { api } from '../../services/api';

interface ShippingZone {
  id: string;
  name: string;
  symbol: string;
  displayAmount: number;
  feeCVE: number;
}

interface StoreData {
  ID: string;
  DESIGNACAO: string;
  DESCRICAO: string | null;
  SLUG: string | null;
  IMAGEM: string | null;
  MIME_TYPE: string | null;
  CAPA: string | null;
  CAPA_MIME_TYPE: string | null;
  TAXA_ENTREGA: number | null;
  SHIPPING_ZONES: ShippingZone[] | null;
}

const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB

function readFileAsBase64(file: File): Promise<{ b64: string; mime: string; dataUrl: string }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const [header, b64] = result.split(',');
      const mime = header.replace('data:', '').replace(';base64', '');
      resolve({ b64, mime, dataUrl: result });
    };
    reader.readAsDataURL(file);
  });
}

const StoreProfile: React.FC = () => {
  const [store, setStore] = useState<StoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const [savingCover, setSavingCover] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [designacao, setDesignacao] = useState('');
  const [descricao, setDescricao] = useState('');
  const [taxaEntrega, setTaxaEntrega] = useState<string>('0');
  // ENTREGA (default) ou LEVANTAMENTO — cantinas/balcões sem entrega
  const [modoEntrega, setModoEntrega] = useState<'ENTREGA' | 'LEVANTAMENTO'>('ENTREGA');
  const [shippingZones, setShippingZones] = useState<ShippingZone[]>([]);

  // Logo
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [logoMime, setLogoMime] = useState<string>('image/png');
  const [logoError, setLogoError] = useState<string | null>(null);

  // Cover/capa
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverBase64, setCoverBase64] = useState<string | null>(null);
  const [coverMime, setCoverMime] = useState<string>('image/jpeg');
  const [coverError, setCoverError] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadStore(); }, []);

  const loadStore = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/portal/loja');
      if (res.data?.success && res.data?.data) {
        const d: StoreData = res.data.data;
        setStore(d);
        setDesignacao(d.DESIGNACAO || '');
        setDescricao(d.DESCRICAO || '');
        setTaxaEntrega(String(d.TAXA_ENTREGA ?? 0));
        setModoEntrega(((d as any).MODO_ENTREGA === 'LEVANTAMENTO' ? 'LEVANTAMENTO' : 'ENTREGA'));
        setShippingZones(Array.isArray(d.SHIPPING_ZONES) ? d.SHIPPING_ZONES : []);
        if (d.IMAGEM && d.MIME_TYPE) setLogoPreview(`data:${d.MIME_TYPE};base64,${d.IMAGEM}`);
        if (d.CAPA && d.CAPA_MIME_TYPE) setCoverPreview(`data:${d.CAPA_MIME_TYPE};base64,${d.CAPA}`);
      } else {
        setError('Loja não encontrada. Certifica-te que tens uma loja ativa.');
      }
    } catch {
      setError('Erro ao carregar perfil da loja.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (
    file: File,
    setPreview: (v: string) => void,
    setB64: (v: string) => void,
    setMime: (v: string) => void,
    setErr: (v: string | null) => void,
    maxSize = MAX_IMAGE_SIZE,
  ) => {
    setErr(null);
    if (!file.type.startsWith('image/')) { setErr('O ficheiro deve ser uma imagem (PNG, JPG, WEBP).'); return; }
    if (file.size > maxSize) { setErr(`A imagem não pode ter mais de ${maxSize / 1024 / 1024}MB.`); return; }
    const { b64, mime, dataUrl } = await readFileAsBase64(file);
    setPreview(dataUrl);
    setB64(b64);
    setMime(mime);
  };

  const handleSaveLogo = async () => {
    if (!logoBase64) return;
    setSavingLogo(true); setError(null);
    try {
      await api.put('/portal/loja/logo', { logo: logoBase64, mimeType: logoMime });
      showSuccess('Logo atualizado com sucesso!');
      setLogoBase64(null);
    } catch { setError('Erro ao guardar logo.'); }
    finally { setSavingLogo(false); }
  };

  const handleSaveCover = async () => {
    if (!coverBase64) return;
    setSavingCover(true); setError(null);
    try {
      await api.put('/portal/loja/cover', { capa: coverBase64, mimeType: coverMime });
      showSuccess('Foto de capa atualizada com sucesso!');
      setCoverBase64(null);
    } catch { setError('Erro ao guardar foto de capa.'); }
    finally { setSavingCover(false); }
  };

  const handleSaveProfile = async () => {
    if (!designacao.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.put('/portal/loja', {
        designacao: designacao.trim(),
        descricao: descricao.trim() || null,
        taxaEntrega: parseFloat(taxaEntrega) || 0,
        shippingZones,
        modoEntrega,
      });
      showSuccess('Perfil atualizado com sucesso!');
    } catch { setError('Erro ao guardar perfil.'); }
    finally { setSaving(false); }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const publicUrl = store?.SLUG ? `${window.location.origin}/loja/${store.SLUG}` : null;

  const handleCopy = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="spinner-lg" />
      </div>
    );
  }

  if (error && !store) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-bold text-pagali-gray-900">Perfil da Loja</h1>
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <FiAlertCircle className="flex-shrink-0 text-lg" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-pagali-gray-900">Perfil da Loja</h1>
        <p className="text-sm text-pagali-gray-500 mt-0.5">
          Personaliza a tua loja — foto de capa, logo, nome e descrição visíveis na página pública.
        </p>
      </div>

      {/* Feedback banners */}
      {successMsg && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          <FiCheck /><span>{successMsg}</span>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <FiAlertCircle /><span>{error}</span>
        </div>
      )}

      {/* Cover Image Card */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {/* Cover preview area */}
        <div
          className="relative w-full bg-gradient-to-br from-pagali-gray-100 to-pagali-gray-200 overflow-hidden cursor-pointer group"
          style={{ height: '160px' }}
          onClick={() => coverInputRef.current?.click()}
          title="Clica para alterar a foto de capa"
        >
          {coverPreview ? (
            <img src={coverPreview} alt="Capa" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-pagali-gray-400 gap-2">
              <FiImage className="text-3xl" />
              <span className="text-xs font-medium">Sem foto de capa</span>
            </div>
          )}
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 bg-white/90 text-pagali-gray-800 text-sm font-medium px-4 py-2 rounded-full shadow">
              <FiCamera />
              Alterar foto de capa
            </div>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-sm font-semibold text-pagali-gray-700 flex items-center gap-2">
                <FiImage />
                Foto de Capa
              </h2>
              <p className="text-xs text-pagali-gray-500 mt-0.5">
                Aparece no topo do card da loja no Pagali Shop. PNG, JPG ou WEBP, máx. 3MB. Recomendado 1200×400px.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => coverInputRef.current?.click()}
              className="text-sm px-4 py-2 border border-pagali-gray-200 rounded-xl text-pagali-gray-700 hover:bg-pagali-gray-50 transition-colors"
            >
              Escolher ficheiro
            </button>
            {coverBase64 && (
              <button
                onClick={handleSaveCover}
                disabled={savingCover}
                className="text-sm px-4 py-2 bg-pagali-primary text-white rounded-xl hover:bg-pagali-primary/90 transition-colors disabled:opacity-60 flex items-center gap-1.5"
              >
                {savingCover ? <div className="spinner-sm" /> : <FiSave />}
                Guardar capa
              </button>
            )}
          </div>
          {coverError && <p className="text-xs text-red-600">{coverError}</p>}
        </div>

        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file, setCoverPreview, setCoverBase64, setCoverMime, setCoverError, 3 * 1024 * 1024);
          }}
        />
      </div>

      {/* Logo Card */}
      <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-pagali-gray-700 flex items-center gap-2">
          <FiCamera />
          Logo da Loja
        </h2>

        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-2xl bg-pagali-gray-50 border-2 border-pagali-gray-100 overflow-hidden flex items-center justify-center">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <IoStorefrontOutline className="text-3xl text-pagali-gray-300" />
              )}
            </div>
            <button
              onClick={() => logoInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-pagali-primary text-white rounded-full flex items-center justify-center shadow-md hover:bg-pagali-primary/90 transition-colors"
            >
              <FiCamera className="text-sm" />
            </button>
          </div>

          <div className="flex-1 space-y-2">
            <p className="text-sm text-pagali-gray-600">PNG, JPG ou WEBP. Máximo 2MB. Recomendamos 400×400px.</p>
            <div className="flex gap-2">
              <button
                onClick={() => logoInputRef.current?.click()}
                className="text-sm px-4 py-2 border border-pagali-gray-200 rounded-xl text-pagali-gray-700 hover:bg-pagali-gray-50 transition-colors"
              >
                Escolher ficheiro
              </button>
              {logoBase64 && (
                <button
                  onClick={handleSaveLogo}
                  disabled={savingLogo}
                  className="text-sm px-4 py-2 bg-pagali-primary text-white rounded-xl hover:bg-pagali-primary/90 transition-colors disabled:opacity-60 flex items-center gap-1.5"
                >
                  {savingLogo ? <div className="spinner-sm" /> : <FiSave />}
                  Guardar logo
                </button>
              )}
            </div>
            {logoError && <p className="text-xs text-red-600">{logoError}</p>}
          </div>
        </div>

        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file, setLogoPreview, setLogoBase64, setLogoMime, setLogoError);
          }}
        />
      </div>

      {/* Profile Info Card */}
      <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
        <h2 className="text-sm font-semibold text-pagali-gray-700 flex items-center gap-2">
          <FiShoppingBag />
          Informações da Loja
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-pagali-gray-600 mb-1">Nome da Loja</label>
            <input
              type="text"
              value={designacao}
              onChange={(e) => setDesignacao(e.target.value)}
              maxLength={100}
              placeholder="Nome da tua loja"
              className="w-full px-3 py-2.5 rounded-xl border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-pagali-gray-600 mb-1">
              Descrição <span className="text-pagali-gray-400">(opcional)</span>
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Descreve os teus produtos, horários de entrega ou qualquer informação relevante..."
              className="w-full px-3 py-2.5 rounded-xl border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary resize-none"
            />
            <p className="text-xs text-pagali-gray-400 mt-1 text-right">{descricao.length}/500</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-pagali-gray-600 mb-1">
              Modo de Entrega
            </label>
            <div className="grid grid-cols-2 gap-2 mb-1">
              {([
                ['ENTREGA', 'Entrega ao domicílio', 'O cliente indica a morada e paga a taxa de entrega'],
                ['LEVANTAMENTO', 'Levantamento no local', 'Sem morada nem taxa — o cliente recolhe na loja (cantinas, balcões)'],
              ] as const).map(([valor, titulo, desc]) => (
                <button
                  key={valor}
                  type="button"
                  onClick={() => setModoEntrega(valor)}
                  className={`text-left p-3 rounded-xl border-2 transition-colors ${
                    modoEntrega === valor
                      ? 'border-pagali-primary bg-pagali-primary/5'
                      : 'border-pagali-gray-200 hover:border-pagali-gray-300'
                  }`}
                >
                  <span className="block text-sm font-semibold text-pagali-gray-800">{titulo}</span>
                  <span className="block text-xs text-pagali-gray-500 mt-0.5">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {modoEntrega === 'ENTREGA' && (
          <div>
            <label className="block text-xs font-medium text-pagali-gray-600 mb-1">
              Taxa de Entrega (ECV) <span className="text-pagali-gray-400">(0 = entrega gratuita)</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={taxaEntrega}
              onChange={(e) => setTaxaEntrega(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2.5 rounded-xl border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-2 focus:ring-pagali-primary/30 focus:border-pagali-primary"
            />
            <p className="text-xs text-pagali-gray-400 mt-1">
              Valor fixo cobrado ao cliente no checkout por encomenda. Define 0 para entrega gratuita.
            </p>
          </div>
          )}

          <button
            onClick={handleSaveProfile}
            disabled={saving || !designacao.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-pagali-primary text-white rounded-xl font-medium text-sm hover:bg-pagali-primary/90 transition-colors disabled:opacity-60"
          >
            {saving ? <div className="spinner-sm" /> : <FiSave />}
            Guardar alterações
          </button>
        </div>
      </div>

      {/* Shipping Zones Card */}
      <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-pagali-gray-700 flex items-center gap-2">
            <FiTruck />
            Zonas de Envio
          </h2>
          <button
            onClick={() => {
              const id = `zone-${Date.now()}`;
              setShippingZones(prev => [...prev, { id, name: '', symbol: '$', displayAmount: 0, feeCVE: 0 }]);
            }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-pagali-gray-200 rounded-lg text-pagali-gray-600 hover:bg-pagali-gray-50 transition-colors"
          >
            <FiPlus className="text-xs" /> Adicionar zona
          </button>
        </div>

        <p className="text-xs text-pagali-gray-500">
          Configura taxas de envio por destino. O cliente escolhe a zona no checkout. O valor em CVE é cobrado no pagamento.
          Se não configurares zonas, é usado o campo "Taxa de Entrega" acima.
        </p>

        {shippingZones.length === 0 ? (
          <p className="text-xs text-pagali-gray-400 italic">Sem zonas configuradas — a usar taxa única acima.</p>
        ) : (
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-pagali-gray-500 px-1">
              <span className="col-span-3">Zona / Destino</span>
              <span className="col-span-2">Símbolo</span>
              <span className="col-span-3">Valor exibido</span>
              <span className="col-span-3">Valor em CVE</span>
              <span className="col-span-1"></span>
            </div>
            {shippingZones.map((zone, idx) => (
              <div key={zone.id} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  value={zone.name}
                  onChange={(e) => setShippingZones(prev => prev.map((z, i) => i === idx ? { ...z, name: e.target.value } : z))}
                  placeholder="ex: United States"
                  className="col-span-3 px-2 py-1.5 rounded-lg border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-1 focus:ring-pagali-primary/40 focus:border-pagali-primary"
                />
                <select
                  value={zone.symbol}
                  onChange={(e) => setShippingZones(prev => prev.map((z, i) => i === idx ? { ...z, symbol: e.target.value } : z))}
                  className="col-span-2 px-2 py-1.5 rounded-lg border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-1 focus:ring-pagali-primary/40 bg-white"
                >
                  <option value="$">$ USD</option>
                  <option value="€">€ EUR</option>
                  <option value="£">£ GBP</option>
                  <option value="CVE">CVE</option>
                </select>
                <input
                  type="number"
                  min="0"
                  value={zone.displayAmount}
                  onChange={(e) => setShippingZones(prev => prev.map((z, i) => i === idx ? { ...z, displayAmount: parseFloat(e.target.value) || 0 } : z))}
                  placeholder="30"
                  className="col-span-3 px-2 py-1.5 rounded-lg border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-1 focus:ring-pagali-primary/40"
                />
                <input
                  type="number"
                  min="0"
                  value={zone.feeCVE}
                  onChange={(e) => setShippingZones(prev => prev.map((z, i) => i === idx ? { ...z, feeCVE: parseFloat(e.target.value) || 0 } : z))}
                  placeholder="3300"
                  className="col-span-3 px-2 py-1.5 rounded-lg border border-pagali-gray-200 text-sm text-pagali-gray-800 focus:outline-none focus:ring-1 focus:ring-pagali-primary/40"
                />
                <button
                  onClick={() => setShippingZones(prev => prev.filter((_, i) => i !== idx))}
                  className="col-span-1 flex items-center justify-center p-1.5 text-pagali-gray-400 hover:text-pagali-error transition-colors"
                >
                  <FiTrash2 className="text-sm" />
                </button>
              </div>
            ))}
          </div>
        )}

        {shippingZones.length > 0 && (
          <button
            onClick={handleSaveProfile}
            disabled={saving || !designacao.trim()}
            className="flex items-center gap-2 px-5 py-2.5 bg-pagali-primary text-white rounded-xl font-medium text-sm hover:bg-pagali-primary/90 transition-colors disabled:opacity-60"
          >
            {saving ? <div className="spinner-sm" /> : <FiSave />}
            Guardar zonas
          </button>
        )}

      </div>

      {/* Public Link Card */}
      <div className="bg-white rounded-2xl shadow-card p-6 space-y-3">
        <h2 className="text-sm font-semibold text-pagali-gray-700 flex items-center gap-2">
          <FiExternalLink />
          Link Público da Loja
        </h2>

        {publicUrl ? (
          <>
            <p className="text-xs text-pagali-gray-500">
              Partilha este link com os teus clientes. Qualquer pessoa pode ver os teus produtos sem precisar de conta.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-pagali-gray-50 border border-pagali-gray-200 rounded-xl min-w-0">
                <span className="text-sm text-pagali-gray-700 truncate flex-1">{publicUrl}</span>
              </div>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  copied ? 'bg-green-500 text-white' : 'bg-pagali-gray-100 text-pagali-gray-700 hover:bg-pagali-gray-200'
                }`}
              >
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-pagali-primary/10 text-pagali-primary hover:bg-pagali-primary/20 transition-colors"
              >
                <FiExternalLink />
                Ver
              </a>
            </div>
          </>
        ) : (
          <p className="text-sm text-pagali-gray-500">
            A tua loja ainda não tem um link público configurado. Contacta o suporte para ativar.
          </p>
        )}
      </div>
    </div>
  );
};

export default StoreProfile;
